import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { PaloColumn, PaloFilterChips, PaloListView, PaloStatusPill, usePaloListSort } from '@synergy/components-palo';
import { ReactElement, useEffect } from 'react';
import { UrlFilter, ValueOrNDash } from '@synergy/components';
import { getDocumentStatusTranslationId, portalStatusColor } from '@synergy/utils';
import { useSearchParams } from 'react-router-dom';

import * as InquiryActions from '../store/inquiry-actions';
import { InquiryListItem } from '../models/inquiry-list-item';
import { PaloInquiryOverviewFilter } from './palo-inquiry-overview-filter';
import { inquiriesSelector } from '../store/inquiry-selectors';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const contactNames = (inquiry: InquiryListItem): string | undefined =>
    inquiry.customerContacts
        ?.map(contact => contact.fullName)
        .filter(Boolean)
        .join(', ');

export const PaloInquiryOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    // The list opens in its own order, and asks the server for it: the header
    // showed "Nummer" with an arrow while the request carried no order at all,
    // so the oldest document stood on top under a header saying the newest
    // (LST-S16, #2234).
    const { sort, params: sortedParams } = usePaloListSort('inquiries');
    const inquiries = useSelector(inquiriesSelector);
    const requesting = useSelector(state => state.inquiry.requesting);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(InquiryActions.fetchInquiries(sortedParams));
    }, [dispatch, sortedParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const columns: PaloColumn<InquiryListItem>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            sortKey: 'number',
            cell: inquiry => inquiry.number,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: inquiry => (
                <PaloStatusPill
                    status={portalStatusColor(inquiry.status)}
                    label={<FormattedMessage id={getDocumentStatusTranslationId(inquiry.status)} />}
                />
            ),
        },
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.table.column.title" />,
            cell: inquiry => inquiry.title,
        },
        {
            key: 'responsibleContact',
            type: 'name',
            head: <FormattedMessage id="app.common.responsibleContact" />,
            variant: 'muted',
            cell: inquiry => <ValueOrNDash value={inquiry.responsibleContact?.fullName} />,
        },
        {
            key: 'customerContact',
            type: 'name',
            head: <FormattedMessage id="app.filter.customerContact" />,
            variant: 'muted',
            cell: inquiry => <ValueOrNDash value={contactNames(inquiry)} />,
        },
        {
            key: 'createdAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.publishDate" />,
            sortKey: 'createdAt',
            cell: inquiry => <FormattedDate value={inquiry.createdAt} year="numeric" month="short" day="2-digit" />,
        },
        {
            key: 'endDate',
            type: 'date',
            head: <FormattedMessage id="app.common.endDate" />,
            sortKey: 'endDate',
            // The legacy table printed createdAt here, so this column repeated
            // the one before it. The model has carried endDate all along.
            cell: inquiry =>
                inquiry.endDate ? (
                    <FormattedDate value={inquiry.endDate} year="numeric" month="short" day="2-digit" />
                ) : (
                    <ValueOrNDash value={undefined} />
                ),
        },
    ];

    return (
        <UrlFilter>
            <PaloListView<InquiryListItem>
                title={<FormattedMessage id="app.navigation.inquiries" />}
                filters={<PaloInquiryOverviewFilter />}
                filterChips={<PaloFilterChips />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                columns={columns}
                items={inquiries.items}
                getRowId={inquiry => inquiry.id}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                pagination={{
                    count: inquiries.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            />
        </UrlFilter>
    );
};
